import { useCallback, useEffect, useRef, useState } from 'react';

import { useApiClient } from '../providers/auth-provider';

import type { CredentialData } from '../lib/types';
import type { CredentialResponse } from '../lib/api-client';

function mapResponse(r: CredentialResponse): CredentialData {
  return {
    object: 'credential',
    id: r.id,
    plan_id: r.plan_id,
    tenant_id: r.tenant_id,
    vault_alias: r.vault_alias,
    service_name: r.service_name,
    access_level: r.access_level,
    access_mode: r.access_mode,
    sharing_mode: r.sharing_mode,
    isolation: r.isolation,
    status: r.status,
    name: r.name,
    credential_type: r.credential_type,
    created_by: r.created_by,
    granted_by: r.granted_by,
    revoked_by: r.revoked_by,
    revocation_reason: r.revocation_reason,
    expires_at: r.expires_at,
    granted_at: r.granted_at,
    revoked_at: r.revoked_at,
    metadata: r.metadata,
    created_at: r.created_at,
    is_enabled_for_tenant: r.is_enabled_for_tenant,
  };
}

export function useSystemCredentials({
  enabled = false,
  pollInterval = 30000,
}: { enabled?: boolean; pollInterval?: number } = {}) {
  const apiClient = useApiClient();
  const [credentials, setCredentials] = useState<CredentialData[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const hasLoadedOnce = useRef(false);
  const fetchInFlightRef = useRef(false);

  const doFetch = useCallback(() => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    apiClient
      .listSystemCredentials()
      .then((res) => {
        setCredentials(res.data.map(mapResponse));
        hasLoadedOnce.current = true;
        setStatus('success');
        setIsRefreshing(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load');
        if (!hasLoadedOnce.current) setStatus('error');
        setIsRefreshing(false);
      })
      .finally(() => {
        fetchInFlightRef.current = false;
      });
  }, [apiClient]);

  const doFetchRef = useRef(doFetch);
  doFetchRef.current = doFetch;

  useEffect(() => {
    if (!enabled) return;
    setStatus('loading');
    doFetchRef.current();
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const interval = setInterval(() => {
      if (fetchInFlightRef.current) return;
      if (typeof document !== 'undefined' && document.hidden) return;
      setIsRefreshing(true);
      doFetchRef.current();
    }, pollInterval);
    return () => clearInterval(interval);
  }, [enabled, pollInterval]);

  const reload = useCallback(() => {
    if (hasLoadedOnce.current) {
      setIsRefreshing(true);
      fetchInFlightRef.current = false;
      doFetchRef.current();
    }
  }, []);

  const enableCredential = useCallback(
    async (id: string) => {
      await apiClient.enableSystemCredential(id);
      setCredentials((prev) =>
        prev.map((c) => (c.id === id ? { ...c, is_enabled_for_tenant: true } : c)),
      );
    },
    [apiClient],
  );

  const disableCredential = useCallback(
    async (id: string) => {
      await apiClient.disableSystemCredential(id);
      setCredentials((prev) =>
        prev.map((c) => (c.id === id ? { ...c, is_enabled_for_tenant: false } : c)),
      );
    },
    [apiClient],
  );

  return {
    credentials,
    status,
    isRefreshing,
    error,
    reload,
    enableCredential,
    disableCredential,
  };
}
